import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import './StaffAugmentation.css';

const roles = [
  { title: 'Incident Response Specialists', titleKey: 'agency.staff_role_incident_response_title', icon: '/images/staff-augmentation-icons/threat-detection.png',      desc: 'Rapid responders who contain, investigate, and eradicate security incidents before damage spreads.', descKey: 'agency.staff_role_incident_response_desc' },
  { title: 'Penetration Testers',           titleKey: 'agency.staff_role_pen_testers_title',          icon: '/images/staff-augmentation-icons/penetration-testing.png',   desc: 'Ethical hackers who uncover vulnerabilities in your systems before real attackers do.', descKey: 'agency.staff_role_pen_testers_desc' },
  { title: 'Security Analysts',             titleKey: 'agency.staff_role_security_analysts_title',    icon: '/images/staff-augmentation-icons/encrypted.png',             desc: 'SOC professionals who monitor, detect, and analyze threats 24/7 across your environment.', descKey: 'agency.staff_role_security_analysts_desc' },
  { title: 'Security Architects',           titleKey: 'agency.staff_role_security_architects_title',  icon: '/images/staff-augmentation-icons/key.png',                   desc: 'Strategic designers who build robust, scalable security frameworks aligned to your goals.', descKey: 'agency.staff_role_security_architects_desc' },
  { title: 'Solution Architects',           titleKey: 'agency.staff_role_solution_architects_title',  icon: '/images/staff-augmentation-icons/atom.png',                  desc: 'Technical leads who design end-to-end IT and cloud solutions with security at the core.', descKey: 'agency.staff_role_solution_architects_desc' },
  { title: 'Network Administrators',        titleKey: 'agency.staff_role_network_admins_title',       icon: '/images/staff-augmentation-icons/network-admin.png',         desc: 'Infrastructure experts who configure, maintain, and secure your network environment.', descKey: 'agency.staff_role_network_admins_desc' },
  { title: 'System Administrators',         titleKey: 'agency.staff_role_system_admins_title',        icon: '/images/staff-augmentation-icons/system-administrator.png',  desc: 'Systems specialists who manage server environments, patching, and hardening at scale.', descKey: 'agency.staff_role_system_admins_desc' },
  { title: 'Business Analysts',             titleKey: 'agency.staff_role_business_analysts_title',    icon: '/images/staff-augmentation-icons/presentation.png',          desc: 'Bridge the gap between business requirements and security solutions with clarity and precision.', descKey: 'agency.staff_role_business_analysts_desc' },
  { title: 'Project Managers',              titleKey: 'agency.staff_role_project_managers_title',     icon: '/images/staff-augmentation-icons/project-team.png',          desc: 'Experienced PMs who deliver cybersecurity and IT projects on time, on scope, on budget.', descKey: 'agency.staff_role_project_managers_desc' },
];

const steps = [
  { n: '01', title: 'Understand Your Needs', titleKey: 'agency.staff_step_understand_title', desc: 'We dive deep into your organizational culture, technical requirements, and security objectives to define the ideal candidate profile.', descKey: 'agency.staff_step_understand_desc' },
  { n: '02', title: 'Source & Vet Talent', titleKey: 'agency.staff_step_source_title', desc: 'Our extensive network of pre-screened professionals is matched against your specific criteria - technical skills, certifications, and cultural fit.', descKey: 'agency.staff_step_source_desc' },
  { n: '03', title: 'Seamless Integration', titleKey: 'agency.staff_step_integration_title', desc: 'We facilitate smooth onboarding and provide ongoing support to ensure your augmented team members hit the ground running.', descKey: 'agency.staff_step_integration_desc' },
  { n: '04', title: 'Continuous Support', titleKey: 'agency.staff_step_support_title', desc: 'Our relationship doesn\'t end at placement. We provide ongoing quality assurance and responsive support throughout the engagement.', descKey: 'agency.staff_step_support_desc' },
];

const values = [
  { icon: '/images/staff-augmentation-icons/staffing.png',         title: 'Expert Staffing Solutions', titleKey: 'agency.staff_value_expert_staffing_title', desc: 'Seasoned professionals with deep industry knowledge across diverse cybersecurity and IT disciplines.', descKey: 'agency.staff_value_expert_staffing_desc' },
  { icon: '/images/staff-augmentation-icons/target.png',          title: 'Client-Centric Approach',   titleKey: 'agency.staff_value_client_centric_title', desc: 'We prioritize understanding your specific needs, culture, and objectives to deliver perfect-fit talent.', descKey: 'agency.staff_value_client_centric_desc' },
  { icon: '/images/staff-augmentation-icons/quality-control.png', title: 'Quality Assurance',         titleKey: 'agency.staff_value_quality_title', desc: 'Rigorous vetting from initial assessment through placement with ongoing support throughout every engagement.', descKey: 'agency.staff_value_quality_desc' },
];

export default function StaffAugmentation() {
  const { t } = useTranslation();
  return (
    <div className="sa-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="container page-hero-content">
          <span className="section-label section-label--blue">{t('agency.staff_hero_badge', 'Talent Solutions')}</span>
          <h1>{t('agency.staff_hero_title', 'Elite')} <span style={{ color: '#d5772d' }}>{t('agency.staff_hero_highlight_title', 'Security Talent')}</span></h1>
          <p>{t('agency.staff_hero_desc', 'Integrate vetted IT and cybersecurity professionals seamlessly into your team. We deliver unparalleled expertise to enhance your capabilities, bridge resource gaps, and drive excellence.')}</p>
        </div>
      </section>

      {/* Value Props */}
      <section className="sa-values">
        <div className="container">
          <div className="sa-values-grid">
            {values.map((v, i) => (
              <div className="sa-value-card" key={i}>
                <div className="sa-value-icon"><img src={v.icon} alt={t(v.titleKey, v.title)} /></div>
                <h3>{t(v.titleKey, v.title)}</h3>
                <p>{t(v.descKey, v.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="sa-roles">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">{t('agency.staff_roles_badge', 'Available Expertise')}</span>
            <h2 className="section-title">{t('agency.staff_roles_title', 'Security Roles We')} <span>{t('agency.staff_roles_highlight_title', 'Place')}</span></h2>
            <p className="section-desc" style={{ margin: '0 auto' }}>
              {t('agency.staff_roles_desc', 'Whether you need a single specialist or an entire security team, we have the talent to meet your requirements.')}
            </p>
          </div>
          <div className="roles-grid">
            {roles.map((role, i) => (
              <div className="role-card" key={i}>
                <div className="role-icon"><img src={role.icon} alt={t(role.titleKey, role.title)} /></div>
                <div className="role-info">
                  <h3>{t(role.titleKey, role.title)}</h3>
                  <p>{t(role.descKey, role.desc)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="sa-process">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">{t('agency.staff_process_badge', 'Our Process')}</span>
            <h2 className="section-title">{t('agency.staff_process_title', 'How We')} <span>{t('agency.staff_process_highlight_title', 'Deliver')}</span></h2>
          </div>
          <div className="process-grid">
            {steps.map((step, i) => (
              <div className="process-card" key={i}>
                <div className="process-number">{step.n}</div>
                <h3>{t(step.titleKey, step.title)}</h3>
                <p>{t(step.descKey, step.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="sa-cta">
        <div className="container">
          <div className="cta-inner">
            <div className="cta-glow" />
            <div className="cta-content">
              <h2>{t('agency.staff_cta_title', 'Ready to Strengthen Your Security Team?')}</h2>
              <p>{t('agency.staff_cta_desc', 'Contact us today to discuss how our Staff Augmentation services can align with your organizational goals and drive excellence in IT and Cyber Security.')}</p>
              <div className="cta-actions">
                <Link to="/jobs" className="btn-outline-white">{t('agency.staff_browse_open_roles_btn', 'Browse Open Roles')}</Link>
                <Link to="/contact" className="btn-primary">
                  {t('agency.staff_discuss_needs_btn', 'Discuss Your Needs')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
